import { KB } from "@constants/fileTypes"
import { EXTENSION_TO_CATEGORY, FILE_SIZE_UNITS, FileCategory, MIME_PREFIX_TO_CATEGORY } from "@constants/fileUpload"

export function getExtensionFromFilename(filename: string): string | null {
    const parts = filename.toLowerCase().split(".")
    if (parts.length < 2) {
        return null
    }

    if (parts.length >= 2 && parts[parts.length - 2] === "tar" && parts[parts.length - 1] === "gz") {
        return "gz"
    }

    return parts[parts.length - 1]
}

export function getFileCategory(mimeType: string, filename?: string): FileCategory {
    const normalizedMime = mimeType.toLowerCase()

    for (const [prefix, category] of Object.entries(MIME_PREFIX_TO_CATEGORY)) {
        if (normalizedMime === prefix || normalizedMime.startsWith(prefix)) {
            return category
        }
    }

    if (filename) {
        const ext = getExtensionFromFilename(filename)
        if (ext && EXTENSION_TO_CATEGORY[ext]) {
            return EXTENSION_TO_CATEGORY[ext]
        }
    }

    if (normalizedMime.includes("word") || normalizedMime.includes("document")) {
        return "word"
    }
    if (normalizedMime.includes("excel") || normalizedMime.includes("spreadsheet")) {
        return "spreadsheet"
    }
    if (normalizedMime.includes("powerpoint") || normalizedMime.includes("presentation")) {
        return "presentation"
    }
    if (
        normalizedMime.includes("zip") ||
        normalizedMime.includes("compressed") ||
        normalizedMime.includes("archive")
    ) {
        return "archive"
    }

    return "unknown"
}

export function isImage(mimeType: string): boolean {
    const category = getFileCategory(mimeType)
    return category === "image" || category === "gif" || category === "svg"
}

export function isVideo(mimeType: string): boolean {
    return getFileCategory(mimeType) === "video"
}

export function isAudio(mimeType: string): boolean {
    return getFileCategory(mimeType) === "audio"
}

export function isPDF(mimeType: string): boolean {
    return getFileCategory(mimeType) === "pdf"
}

/**
 * Format bytes into a human-readable file size string
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) {
        return "0 Bytes"
    }
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(KB)), FILE_SIZE_UNITS.length - 1)
    const size = parseFloat((bytes / Math.pow(KB, unitIndex)).toFixed(1))
    return `${size} ${FILE_SIZE_UNITS[unitIndex]}`
}

/**
 * Generate a preview URL for an image file using FileReader
 */
export function generateImagePreview(file: File): Promise<string | undefined> {
    return new Promise((resolve) => {
        if (!isImage(file.type)) {
            resolve(undefined)
            return
        }

        const reader = new FileReader()
        reader.onload = (event) => {
            resolve(event.target?.result as string)
        }
        reader.onerror = () => {
            resolve(undefined)
        }
        reader.readAsDataURL(file)
    })
}
